import { assignSafe, isString } from '@summit/core';
import { isNumber, SummitFormattedText } from '@summit/models';
import { RoleAccess, RoleAccessMapping } from '../policies/role-access.enum';
import { ScheduleViewOfBoard } from './schedule-view-of-board.enum';

export class ScheduleParameter {
  schedulingEnabled: boolean = null;
  liveDispatchEnabled: boolean = null;
  driversView: ScheduleViewOfBoard = null;
  driverNotifications: boolean = null;
  addAdminAsApprovers: boolean = null;
  disableNotificationDateHeader: boolean = null;
  addFirstLegToNotification: boolean = null;
  addLiveDispatchDetails: boolean = null;
  disableFirstLegOnDedicated: boolean = null;
  addRouteHubAbbreviation: boolean = null;
  limitToAffectedDays: boolean = null;
  timeOffEdit: RoleAccess = null;
  routeInfoEdit: RoleAccess = null;

  constructor(jsonData?: Partial<ScheduleParameter>) {
    if (jsonData) {
      assignSafe(this, jsonData);
      this.timeOffEdit = this.getEnumValue(jsonData.timeOffEdit);
      this.routeInfoEdit = this.getEnumValue(jsonData.routeInfoEdit);
    }
  }

  private getEnumValue(value: string | number | null): number {
    if (value === undefined || value === null || (isString(value) && value == '')) return null;
    if (isNumber(value)) return value;
    return parseInt(value);
  }

  isEquivalent(other: ScheduleParameter): boolean {
    return (
      this.schedulingEnabled === other.schedulingEnabled &&
      this.liveDispatchEnabled === other.liveDispatchEnabled &&
      this.driversView === other.driversView &&
      this.driverNotifications === other.driverNotifications &&
      this.addAdminAsApprovers === other.addAdminAsApprovers &&
      this.disableNotificationDateHeader === other.disableNotificationDateHeader &&
      this.addFirstLegToNotification === other.addFirstLegToNotification &&
      this.addLiveDispatchDetails === other.addLiveDispatchDetails &&
      this.disableFirstLegOnDedicated === other.disableFirstLegOnDedicated &&
      this.limitToAffectedDays === other.limitToAffectedDays &&
      this.addRouteHubAbbreviation === other.addRouteHubAbbreviation &&
      this.timeOffEdit === other.timeOffEdit &&
      this.routeInfoEdit === other.routeInfoEdit
    );
  }

  setCorporateDefaults() {
    this.schedulingEnabled ??= true;
    this.liveDispatchEnabled ??= false;
    this.driversView ??= ScheduleViewOfBoard.None;
    this.driverNotifications ??= false;
    this.addAdminAsApprovers ??= false;
    this.disableNotificationDateHeader ??= false;
    this.addFirstLegToNotification ??= false;
    this.addLiveDispatchDetails ??= false;
    this.disableFirstLegOnDedicated ??= true;
    this.limitToAffectedDays ??= false;
    this.addRouteHubAbbreviation ??= false;
    this.timeOffEdit ??= RoleAccess.BackOfficeAndAbove;
    this.routeInfoEdit ??= RoleAccess.BackOfficeAndAbove;
  }

  private _summary: SummitFormattedText;
  getSummary(): SummitFormattedText {
    if (!this._summary) {
      this._summary = new SummitFormattedText();
      if (this.schedulingEnabled == true) {
        this._summary.AddLine(`Scheduling Enabled`, ['flex', 'flex-row', 'justify-center', 'font-bold']);
        if (this.liveDispatchEnabled == true) {
          this._summary.AddKeyValues(`Live Dispatch`, 'Enabled');
        } else if (this.liveDispatchEnabled == false) {
          this._summary.AddKeyValues(`Live Dispatch`, 'Disabled');
        } else this._summary.AddKeyValues(`Live Dispatch`, '[Corp Default]');
        if (this.driverNotifications == true) {
          this._summary.AddKeyValues(`Driver Notifications`, 'Enabled');
        } else if (this.driverNotifications == false) {
          this._summary.AddKeyValues(`Driver Notifications`, 'Disabled');
        } else this._summary.AddKeyValues(`Driver Notifications`, '[Corp Default]');
        if (this.disableNotificationDateHeader == true) {
          this._summary.AddKeyValues(`Notifications Date Header`, 'Disabled');
        } else if (this.disableNotificationDateHeader != false) {
          this._summary.AddKeyValues(`Notifications Date Header`, '[Corp Default]');
        }
        if (this.addRouteHubAbbreviation == true) {
          this._summary.AddKeyValues(`Add Route Hub Abbr.`, 'Enabled');
        } else if (this.addRouteHubAbbreviation != false) {
          this._summary.AddKeyValues(`Add Route Hub Abbr.`, '[Corp Default]');
        }
        if (this.addFirstLegToNotification == true) {
          this._summary.AddKeyValues(`Add First Leg?`, 'Enabled');
          if (this.disableFirstLegOnDedicated == true) {
            this._summary.AddKeyValues(`Disable First Leg on Dedicated?`, 'Enabled');
          } else if (this.disableFirstLegOnDedicated != false) {
            this._summary.AddKeyValues(`Disable First Leg on Dedicated?`, '[Corp Default]');
          }
        } else if (this.addFirstLegToNotification != false) {
          this._summary.AddKeyValues(`Driver Notifications`, '[Corp Default]');
        }
        if (this.addLiveDispatchDetails == true) {
          this._summary.AddKeyValues(`Add Live Dispatch Details`, 'Enabled');
        } else if (this.addLiveDispatchDetails != false) {
          this._summary.AddKeyValues(`Add Live Dispatch Details`, '[Corp Default]');
        }
        if (this.limitToAffectedDays == true) {
          this._summary.AddKeyValues(`Limit to only Changes`, 'Enabled');
        } else if (this.limitToAffectedDays != false) {
          this._summary.AddKeyValues(`Limit to only Changes`, '[Corp Default]');
        }
      } else if (this.schedulingEnabled == false) {
        this._summary.AddLine(`Scheduling Disabled`, ['flex', 'flex-row', 'justify-center', 'font-bold']);
      }
      switch (this.driversView) {
        case ScheduleViewOfBoard.None:
          this._summary.AddKeyValues(`Drivers' View`, `Own Schedule`);
          break;
        case ScheduleViewOfBoard.All:
          this._summary.AddKeyValues(`Drivers' View`, `All Schedules`);
          break;
        case ScheduleViewOfBoard.TBD:
          this._summary.AddKeyValues(`Drivers' View`, `Own Schedule + TBD`);
          break;
        case ScheduleViewOfBoard.TBDandDown:
          this._summary.AddKeyValues(`Drivers' View`, `Own Schedule + TBD & Down`);
          break;
        case ScheduleViewOfBoard.AllButTBDandDown:
          this._summary.AddKeyValues(`Drivers' View`, `All except TBD & Down`);
          break;
        default:
          break;
      }
      if (this.addAdminAsApprovers == true) {
        this._summary.AddKeyValues(`Time Off`, `Admins as approvers`);
      } else if (this.addAdminAsApprovers == null) {
        this._summary.AddKeyValues(`Time Off`, `[Corp default]`);
      }
      if (this.routeInfoEdit == RoleAccess.None || this.routeInfoEdit) {
        this._summary.AddKeyValues(`Route Info Edit`, RoleAccessMapping[this.routeInfoEdit]);
      } else if (this.routeInfoEdit == null) {
        this._summary.AddKeyValues(`Route Info Edit`, `[Corp default]`);
      }

      if (this.timeOffEdit == RoleAccess.None || this.timeOffEdit) {
        this._summary.AddKeyValues(`Time Off Edit`, RoleAccessMapping[this.timeOffEdit]);
      } else if (this.timeOffEdit == null) {
        this._summary.AddKeyValues(`Time Off Edit`, `[Corp default]`);
      }
    }

    return this._summary;
  }
}
